import React, {
  useState,
  useCallback,
  lazy,
  Suspense,
} from "react";

import {
  AppBar,
  Box,
  Button,
  Badge,
  Stack,
  Typography,
  Popover,
  IconButton,
  Drawer,
  Paper,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { IoMdAdd } from "react-icons/io";

import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import { RiMenu3Line } from "react-icons/ri";
import { IoIosArrowDown } from "react-icons/io";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FiSearch, FiArrowUpRight } from "react-icons/fi";
import { Logout } from "@mui/icons-material";

import { useAuth } from "../hooks/authContext";
import { useCity } from "../hooks/cityContext";
import { navStructure } from "./constants/properties";
import { IterationCcwIcon } from "lucide-react";

const RegisterForm = lazy(() => import("../pages/signup"));
const MyMenu = lazy(() => import("./MyComps/menu"));
const SelectCity = lazy(() => import("./MyComps/selectCity"));
const SearchBar2 = lazy(() => import("./MyComps/searchBar2"));
const SidebarList = lazy(() => import("../Dropdown/NavList"));
const BottonNavLinks = lazy(() => import("./BottonNavLinks"));

const PRIMARY = "#ED213A";
const DARK = "#252525";
const MUTED = "#777777";

function Navbar() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const showSearchBar = location.pathname === "/";

  const { user, logout } = useAuth();
  const { selectedCity, changeCity } = useCity();

  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cityAnchorEl, setCityAnchorEl] = useState(null);

  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCityOpen = Boolean(cityAnchorEl);

  const handleCityClick = useCallback((event) => {
    setCityAnchorEl(event.currentTarget);
  }, []);

  const handleCityClose = useCallback(() => {
    setCityAnchorEl(null);
  }, []);

  const onCitySelect = useCallback(
    (city) => {
      changeCity(city);
      handleCityClose();
    },
    [changeCity, handleCityClose]
  );

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } finally {
      setDrawerOpen(false);
    }
  }, [logout]);

  const handlePostProperty = useCallback(() => {
    if (user) {
      navigate("/post-properties/basic-info");
    } else {
      setOpen(true);
    }
  }, [user, navigate]);

  const logoSrc ="/gharnmakaan.webp"
 

  const navLinkSx = {
    position: "relative",
    color: "#454545",
    textDecoration: "none",
    fontSize: "0.9rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    transition: "color 0.2s ease",
    "&:hover": {
      color: PRIMARY,
    },
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          color: DARK,
          boxShadow: "0 3px 24px rgba(0,0,0,0.045)",
          borderBottom: "1px solid #F0F0F0",
          zIndex: theme.zIndex.appBar,
        }}
      >
        {open && (
          <Suspense fallback={null}>
            <RegisterForm
              open={open}
              handleClose={() => setOpen(false)}
              check={true}
            />
          </Suspense>
        )}

        {/* Main header */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 1600,
            mx: "auto",
            minHeight: { xs: 66, md: 82 },
            px: { xs: 1, sm: 2.5, md: 3, lg: 4 },
            py: { xs: 0.75, md: 1.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 0.5, md: 2 },
          }}
        >
          {/* Left: menu + brand + location */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1.5, md: 2 }}
            sx={{ flexShrink: 0 }}
          >
            <IconButton
              aria-label="Open navigation menu"
              onClick={() => setDrawerOpen(true)}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                color: DARK,
                width: 40,
                height: 40,
                borderRadius: "12px",
                "&:hover": {
                  bgcolor: "#FFF0F2",
                  color: PRIMARY,
                },
              }}
            >
              <RiMenu3Line size={22} />
            </IconButton>

            <Link
              to="/"
              aria-label="Gharnmakaan home"
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt="Gharnmakaan.com"
                width={160}
                height={48}
                fetchPriority="high"
                decoding="async"
                sx={{
                  display: "block",
                  width: {
                    xs: 100,
                    sm: 130,
                    md: 145,
                    lg: 155,
                  },
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Link>

            {/* Location selector */}
            <Box
              onClick={handleCityClick}
              role="button"
              tabIndex={0}
              aria-label={`Select city, current city ${selectedCity || "All India"}`}
              aria-haspopup="dialog"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleCityClick({
                    currentTarget: event.currentTarget,
                  });
                }
              }}
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderLeft: { sm: "1px solid #EEEEEE" },
                pl: { sm: 1.5, md: 2 },
                ml: { sm: 0.5 },
                minWidth: 0,
                "&:hover .location-title": {
                  color: PRIMARY,
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: 35,
                  height: 35,
                  bgcolor: "#FFF0F2",
                  color: PRIMARY,
                  borderRadius: "11px",
                  flexShrink: 0,
                }}
              >
                <FaMapMarkerAlt size={15} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.67rem",
                    color: "#929292",
                    lineHeight: 1.3,
                  }}
                >
                  Your location
                </Typography>

                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    className="location-title"
                    sx={{
                      color: DARK,
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      maxWidth: { sm: 90, md: 115 },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {selectedCity || "All India"}
                  </Typography>

                  <IoIosArrowDown size={13} />
                </Stack>
              </Box>
            </Box>

            <Popover
              open={isCityOpen}
              anchorEl={cityAnchorEl}
              onClose={handleCityClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1.5,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                  },
                },
              }}
            >
              <Suspense fallback={null}>
                <SelectCity onCitySelect={onCitySelect} />
              </Suspense>
            </Popover>
          </Stack>

          {/* Center: desktop navigation or search */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              minWidth: 0,
              px: { md: 1, lg: 2 },
            }}
          >
            {!showSearchBar ? (
              <Box sx={{ width: "100%", maxWidth: 500 }}>
                <Suspense fallback={null}>
                  <SearchBar2
                    isMobile={isMobile}
                    isTablet={isTablet}
                  />
                </Suspense>
              </Box>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ md: 1.5, lg: 2.5 }}
                sx={{ whiteSpace: "nowrap" }}
              >
                {navStructure.map((item) => (
                  <Box
                    key={item.label}
                    className="modern-nav-item"
                    sx={{
                      position: "relative",
                      py: 1.5,
                      "&:hover .modern-nav-dropdown": {
                        opacity: 1,
                        visibility: "visible",
                        transform: "translateY(0)",
                      },
                      "&:hover .modern-nav-label": {
                        color: PRIMARY,
                      },
                    }}
                  >
                    <Stack
                      component="button"
                      direction="row"
                      alignItems="center"
                      spacing={0.6}
                      className="modern-nav-label"
                      sx={{
                        border: 0,
                        bgcolor: "transparent",
                        cursor: "pointer",
                        p: 0,
                        color: DARK,
                        font: "inherit",
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 550,
                        transition: "color 0.2s ease",
                        gap: 1
                      }}
                    >
                      {item.label}
                      <IoIosArrowDown size={13} />
                    </Stack>

                    <Box
                      className="modern-nav-dropdown"
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: -15,
                        minWidth: 220,
                        p: 1,
                        bgcolor: "#FFFFFF",
                        border: "1px solid #F0F0F0",
                        borderRadius: "14px",
                        boxShadow: "0 16px 40px rgba(0,0,0,0.11)",
                        opacity: 0,
                        visibility: "hidden",
                        transform: "translateY(8px)",
                        transition:
                          "opacity 0.2s ease, transform 0.2s ease, visibility 0.2s",
                        zIndex: 1400,
                      }}
                    >
                      {item.subItems.map((subItem) => (
                        <Box
                          key={subItem.path}
                          component={Link}
                          to={subItem.path}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            px: 1.5,
                            py: 1.2,
                            borderRadius: "9px",
                            color: "#555555",
                            fontSize: "0.86rem",
                            textDecoration: "none",
                            "&:hover": {
                              color: PRIMARY,
                              bgcolor: "#FFF3F4",
                            },
                          }}
                        >
                          {subItem.label}
                          <FiArrowUpRight size={14} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Right: search shortcut on mobile + post property + account */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1, md: 1.5 }}
            sx={{ flexShrink: 0, ml: "auto" }}
          >
            {showSearchBar && (
              <IconButton
                aria-label="Search properties"
                onClick={() => navigate("/search")}
                sx={{
                  display: { xs: "inline-flex", md: "none" },
                  color: PRIMARY,
                  width: 38,
                  height: 38,
                  border: "1px solid #F4DADD",
                  borderRadius: "12px",
                }}
              >
                <FiSearch size={19} />
              </IconButton>
            )}

            <Badge
              badgeContent="FREE"
              overlap="rectangular"
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: "#198754",
                  color: "#FFFFFF",
                  fontSize: "0.55rem",
                  fontWeight: 800,
                  height: 17,
                  minWidth: 32,
                  px: 0.5,
                  borderRadius: "5px",
                  top: 0,
                  right: 8,
                  border: "2px solid #FFFFFF",
                },
              }}
            >
            <Button
  onClick={handlePostProperty}
  variant="contained"
  disableElevation
  sx={{
    position: "relative",
    bgcolor: PRIMARY,
    color: "#FFFFFF",
    textTransform: "none",
    fontSize: { xs: "0.76rem", sm: "0.86rem" },
    fontWeight: 550,
    borderRadius: "8px",
    px: { xs: 1.2, sm: 2, md: 2.3 },
    py: { xs: 1.05, md: 1.3 },
    minWidth: 0,
    whiteSpace: "nowrap",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 0.75,
    lineHeight: 1.5,
    boxShadow: "0 4px 12px rgba(237,33,58,0.15)",
    "&:hover": {
      bgcolor: "#D71931",
      boxShadow: "0 6px 16px rgba(237,33,58,0.22)",
      transform: "translateY(-1px)",
    },
    transition: "all 0.2s ease",
  }}
>
  <Box
    component="span"
    sx={{
      display: { xs: "none", sm: "flex" },
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 0,
    }}
  >
    <IoMdAdd size={18} />
  </Box>

  <Box component="span" sx={{ lineHeight: 1.5 }}>
    Post Property
  </Box>
</Button>
            </Badge>

            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Suspense fallback={null}>
                <MyMenu color={DARK} />
              </Suspense>
            </Box>
          </Stack>
        </Box>

        {/* Mobile search row on inner pages */}
        {/* {!showSearchBar && (
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              px: 2,
              pb: 1.5,
            }}
          >
            <Suspense fallback={null}>
              <SearchBar2
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </Suspense>
          </Box>
        )} */}
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: "85vw", sm: 340 },
            maxWidth: 360,
            bgcolor: "#FFFFFF",
            borderRadius: "0 20px 20px 0",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box
            component="img"
            src="/gharnmakaan.webp"
            alt="Gharnmakaan.com"
            sx={{
              width: 150,
              height: "auto",
              objectFit: "contain",
            }}
          />

          <IconButton
            aria-label="Close navigation menu"
            onClick={() => setDrawerOpen(false)}
            sx={{
              bgcolor: "#FFF0F2",
              color: PRIMARY,
              "&:hover": { bgcolor: "#FFE0E5" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: "#F0F0F0" }} />

        <Box sx={{ py: 1, flex: 1, overflowY: "auto" }}>
          <Suspense fallback={null}>
            <SidebarList />
          </Suspense>
        </Box>

        {user && (
          <Box sx={{ p: 2 }}>
            <Button
              startIcon={<Logout />}
              onClick={handleLogout}
              fullWidth
              sx={{
                justifyContent: "flex-start",
                borderRadius: "12px",
                color: PRIMARY,
                bgcolor: "#FFF0F2",
                py: 1.3,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { bgcolor: "#FFE0E5" },
              }}
            >
              Log Out
            </Button>
          </Box>
        )}
      </Drawer>

      {/* Existing mobile bottom navigation */}
      {isMobile && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar + 1,
            bgcolor: "transparent",
          }}
        >
          <Suspense fallback={null}>
            <BottonNavLinks />
          </Suspense>
        </Paper>
      )}
    </>
  );
}

export default React.memo(Navbar);
